var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
/*
 * <vitesse-shell> — THE shared app shell every Vitesse product UI reuses. It is the
 * base page's chrome, lifted into base-ui so it's GUARANTEED identical everywhere:
 *   - the real <vitesse-header> (same icons/order/placement as the gateway app)
 *   - a collapsible 250px LEFT tree rail (data-driven <vitesse-nav-tree>)
 *   - a center content area (default <slot>)
 *   - a collapsible 360px RIGHT AI rail (sparkles + ai.title header; body defaults to
 *     supplied by the app via <slot name="ai">)
 *
 * What VARIES per app is only the content you slot in: `treeNodes` (the tree data),
 * the default slot (center), and the header's `notifications`/`org`/`user` slots —
 * which the shell forwards straight to <vitesse-header>. The header dispatches
 * `vitesse-toggle-tree` / `vitesse-toggle-ai`; the shell flips the rails.
 *
 * NOT in the main barrel (imported via `@vitesse/base-ui/elements/vitesse-shell`) so
 * it doesn't transitively register <vitesse-header> into apps that have their own.
 */
import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { t } from '../i18n';
import { LocaleController } from '../i18n/LocaleController';
import './vitesse-header';
import './vitesse-icon';
import './vitesse-nav-tree';
import './vitesse-ai-rail';
import { AiBusyTally, onToggleAi, onToggleTree } from './rail-resize';
let VitesseShell = class VitesseShell extends LitElement {
    constructor() {
        super();
        /** The left-tree hierarchy. The app builds this from its own data. */
        this.treeNodes = [];
        /** Selected tree node key (drives the tree's `.active`). */
        this.selectedKey = '';
        /** Left tree rail open (default open). */
        this.treeOpen = true;
        /** Right AI rail open (default closed). */
        this.aiOpen = false;
        /*
         * WHAT THE HEADER SHOWS WHILE THE RAIL IS SHUT.
         *
         * The rail collapses to zero width rather than unmounting, so the assistant keeps working when it
         * is closed — which is the reason to close it. Without a signal in the header there is no way to
         * tell that from having closed a finished thread, so the panel announces activity and this holds
         * the two bits the button needs: working now, and finished-while-you-were-away.
         *
         * It sits on the SHELL, next to `aiOpen`, because the shell is what owns the rail. There are two
         * shells — this one (the console and the fleet app) and the gateway app's own — and each carries
         * this the same way it carries `aiOpen`. It was wired into only the gateway app's first, which is
         * why the console showed nothing.
         */
        this.aiBusy = false;
        this.aiUnseen = false;
        this.busyTally = new AiBusyTally();
        this.onAiActivity = (e) => {
            // Folded into the tally, because several surfaces report at once — see AiBusyTally.
            this.busyTally.note(e.detail.id ?? 'anon', e.detail.busy);
            const busy = this.busyTally.working;
            // Finishing UNSEEN is the interesting transition — the moment a shut rail becomes worth
            // reopening. Finishing while it is open is just the answer arriving.
            if (this.aiBusy && !busy && !this.aiOpen)
                this.aiUnseen = true;
            this.aiBusy = busy;
        };
        /* Both panes move through the shared rule so the two shells cannot disagree about it. */
        this.toggleAi = () => {
            const next = onToggleAi({ treeOpen: this.treeOpen, aiOpen: this.aiOpen });
            this.treeOpen = next.treeOpen;
            this.aiOpen = next.aiOpen;
            if (this.aiOpen)
                this.aiUnseen = false; // looked at it
        };
        this.toggleTree = () => {
            this.treeOpen = onToggleTree({ treeOpen: this.treeOpen, aiOpen: this.aiOpen }).treeOpen;
        };
        new LocaleController(this); // re-render the AI rail labels on language change
    }
    static { this.styles = css `
    :host {
      display: flex;
      flex-direction: column;
      height: 100vh;
      background: var(--bg-primary);
      color: var(--text-primary);
    }
    .main-layout {
      flex: 1;
      min-height: 0;
      display: flex;
    }
    /* Left tree rail — width-animated show/hide via the header's list-tree button. */
    .tree-rail {
      width: 250px;
      flex-shrink: 0;
      border-right: 1px solid var(--border-color);
      overflow: hidden;
      transition: width 0.22s ease;
    }
    :host(:not([treeopen])) .tree-rail {
      width: 0;
      border-right-width: 0;
    }
    .tree-inner {
      width: 250px;
      height: 100%;
    }
    .main-content {
      flex: 1;
      min-width: 0;
      overflow-y: auto;
    }
    /* Right AI rail — width-animated show/hide via the header's AI button. */
  `; }
    /**
     * Announce that the AI rail has OPENED.
     *
     * The shell owns `aiOpen`, so without this an app that mounts its rail through `slot="ai"` cannot
     * tell when the user actually wants it — and the AI handshake is deliberately deferred to that
     * moment, because minting an ephemeral token at boot costs every session that never opens the
     * rail. Emitted from `updated` rather than the toggle handler so it fires however the rail opened:
     * the header button, the `vitesse-ai-prompt` shortcut, or a host setting `.aiOpen` directly.
     *
     * `composed` so it crosses the shadow boundary to whatever hosts the shell.
     */
    updated(changed) {
        if (changed.has('aiOpen') && this.aiOpen) {
            this.dispatchEvent(new CustomEvent('vitesse-ai-open', { bubbles: true, composed: true }));
        }
    }
    render() {
        return html `
      <vitesse-header
        .notifications=${this.notifications}
        .aiOpen=${this.aiOpen}
        .aiBusy=${this.aiBusy}
        .aiDone=${this.aiUnseen}
        @vitesse-toggle-tree=${this.toggleTree}
        @vitesse-toggle-ai=${this.toggleAi}
      >
        <slot name="org" slot="org"></slot>
        <slot name="user" slot="user"></slot>
      </vitesse-header>
      <div class="main-layout" @vitesse-ai-prompt=${() => (this.aiOpen = true)} @vitesse-ai-activity=${this.onAiActivity}>
        <div class="tree-rail">
          <div class="tree-inner">
            <vitesse-nav-tree .nodes=${this.treeNodes} selectedKey=${this.selectedKey}></vitesse-nav-tree>
          </div>
        </div>
        <div class="main-content"><slot></slot></div>
        <vitesse-ai-rail ?open=${this.aiOpen}>
          <!-- No fallback: an app that opens the rail without supplying a panel should show an
               empty rail, not a placeholder that looks like a shipped feature. -->
          <slot name="ai"></slot>
        </vitesse-ai-rail>
      </div>
    `;
    }
};
__decorate([
    property({ attribute: false })
], VitesseShell.prototype, "treeNodes", void 0);
__decorate([
    property()
], VitesseShell.prototype, "selectedKey", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], VitesseShell.prototype, "treeOpen", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], VitesseShell.prototype, "aiOpen", void 0);
__decorate([
    property({ attribute: false })
], VitesseShell.prototype, "notifications", void 0);
__decorate([
    state()
], VitesseShell.prototype, "aiBusy", void 0);
__decorate([
    state()
], VitesseShell.prototype, "aiUnseen", void 0);
VitesseShell = __decorate([
    customElement('vitesse-shell')
], VitesseShell);
export { VitesseShell };
//# sourceMappingURL=vitesse-shell.js.map